<template>
  <BaseFetchData :action="fetchData">
    <!-- props
      fetchData, status.loading, status.resolved, status.rejected, rejectReason
    -->
    <template #default="fetchDataProps">
      <div v-if="fetchDataProps.status.resolved">
        <BaseVeeForm
          :action="updateAccountMembershipProduct"
          @success="fetchAccountMembershipProduct"
        >
          <!-- formProps
            isIdle, isPending, isResolved, isRejected, invalid
            errors, submit, hasServerErrors, getServerErrorMessages
          -->
          <template #default="formProps">
            <BaseCard>
              <BaseCardBody class="space-y-6">
                <ValidationProvider name="membership product" slim vid="membership_product_id">
                  <template #default="{ errors }">
                    <BaseFormGroup>
                      <BaseFormLabel>Current membership product</BaseFormLabel>
                      <VSelect
                        v-model="currentMembershipProduct"
                        :options="membershipProductOptions"
                        placeholder="None"
                        label="label"
                        :disabled="formProps.isPending"
                        :filterable="false"
                        class="vue-select max-w-lg"
                        @search="onSearch"
                      >
                        <template v-if="hasMore" #list-footer>
                          <li class="flex space-x-2 px-2 pt-2 mb-1">
                            <BaseButton
                              primary
                              outline
                              small
                              :disabled="!hasPrevPage"
                              class="flex-1"
                              @click="fetchPrevPage"
                            >
                              Prev
                            </BaseButton>
                            <BaseButton
                              primary
                              outline
                              small
                              :disabled="!hasNextPage"
                              class="flex-1"
                              @click="fetchNextPage"
                            >
                              Next
                            </BaseButton>
                          </li>
                        </template>
                      </VSelect>
                      <BaseFormInlineMessage v-if="errors.length" danger>
                        {{ errors[0] }}
                      </BaseFormInlineMessage>
                    </BaseFormGroup>
                  </template>
                </ValidationProvider>
                <BaseButton primary>Update</BaseButton>
              </BaseCardBody>
            </BaseCard>
          </template>
        </BaseVeeForm>
      </div>
    </template>
  </BaseFetchData>
</template>

<script>
import { debounce } from 'lodash-es';
import { accountMembershipProductClient, accountProductsClient } from '@/api/clients';
import { toOptions } from '@/utils/helpers';

export default {
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentMembershipProduct: null,
      productsData: null,
      queryParams: {
        filter: {
          search: '',
          type: 'membership',
        },
        page: 1,
      },
    };
  },
  computed: {
    account() {
      return this.$store.state.entities.accounts.byId[this.accountId];
    },
    membershipProductOptions() {
      if (Array.isArray(this.productsData.data) === false) {
        return [];
      }

      return toOptions(this.productsData.data, 'id', 'name');
    },
    hasPrevPage() {
      return (this.productsData?.meta?.current_page || 1) > 1;
    },
    hasNextPage() {
      return (
        (this.productsData?.meta?.last_page || 1) > (this.productsData?.meta?.current_page || 1)
      );
    },
    hasMore() {
      return this.productsData.meta?.last_page > 1;
    },
  },
  methods: {
    fetchData() {
      return Promise.all([this.fetchAccountMembershipProduct(), this.fetchMembershipProducts()]);
    },
    fetchAccountMembershipProduct() {
      return accountMembershipProductClient.read(this.accountId, {}).then((r) => {
        this.currentMembershipProduct = r.data ? toOptions([r.data], 'id', 'name')[0] : null;
      });
    },
    fetchMembershipProducts() {
      return accountProductsClient.list(this.accountId, this.queryParams).then((response) => {
        this.productsData = response;
      });
    },
    onSearch(search, loading) {
      loading(true);
      this.searchOptions(loading, search, this);
    },
    searchOptions: debounce((loading, search, vm) => {
      // eslint-disable-next-line no-param-reassign
      vm.queryParams.filter.search = search;
      vm.fetchMembershipProducts().finally(() => loading(false));
    }, 350),
    fetchPrevPage() {
      if (!this.hasPrevPage) {
        return;
      }
      this.queryParams.page = this.productsData.meta.current_page - 1;
      this.fetchMembershipProducts();
    },
    fetchNextPage() {
      if (!this.hasNextPage) {
        return;
      }
      this.queryParams.page = this.productsData.meta.current_page + 1;
      this.fetchMembershipProducts();
    },
    updateAccountMembershipProduct() {
      const data = {
        membership_product_id: this.currentMembershipProduct?.value || null,
      };

      return accountMembershipProductClient.update(this.accountId, data).then(() => {
        this.fetchAccountMembershipProduct();
      });
    },
  },
};
</script>
